import { RefreshCw } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import type { IncomeStatement } from '../../types/drawdown';

export function StatementOfOperations({
  incomeStatement,
  isLoading,
}: {
  incomeStatement: IncomeStatement | null | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="border rounded-lg bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            STATEMENT OF OPERATIONS (INCOME STATEMENT)
          </h2>
        </div>
        <div className="p-6">
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            Loading income statement...
          </div>
        </div>
      </div>
    );
  }

  if (!incomeStatement) {
    return (
      <div className="border rounded-lg bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            STATEMENT OF OPERATIONS (INCOME STATEMENT)
          </h2>
        </div>
        <div className="p-6">
          <div className="text-center py-8 text-muted-foreground">
            No income statement data
          </div>
        </div>
      </div>
    );
  }

  const incomeAccounts = Object.values(incomeStatement.income);
  const expenseAccounts = Object.values(incomeStatement.expenses);

  return (
    <div className="border rounded-lg bg-card">
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-semibold">
          STATEMENT OF OPERATIONS (INCOME STATEMENT)
        </h2>
      </div>
      <div className="p-6 space-y-6">
        {/* Revenues Section */}
        <div>
          <h3 className="font-semibold text-foreground mb-3">REVENUES</h3>
          <div className="space-y-2">
            {incomeAccounts.map((account) => (
              <div
                key={account.account_code}
                className="flex justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  {account.account_name} ({account.account_code})
                </span>
                <span>{formatCurrency(Math.abs(account.net_balance))}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-semibold mt-3 pt-2 border-t">
            <span>Total Revenue</span>
            <span>{formatCurrency(incomeStatement.totals.total_income)}</span>
          </div>
        </div>

        {/* Expenses Section */}
        <div>
          <h3 className="font-semibold text-foreground mb-3">EXPENSES</h3>
          <div className="space-y-2">
            {expenseAccounts.map((account) => (
              <div
                key={account.account_code}
                className="flex justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  {account.account_name} ({account.account_code})
                </span>
                <span>{formatCurrency(account.net_balance)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-semibold mt-3 pt-2 border-t">
            <span>Total Expenses</span>
            <span>{formatCurrency(incomeStatement.totals.total_expenses)}</span>
          </div>
        </div>

        {/* Net Income */}
        <div className="bg-muted -mx-6 px-6 py-4 mt-6">
          <div className="flex justify-between font-semibold text-lg">
            <span>Net Income (Loss)</span>
            <span>{formatCurrency(incomeStatement.totals.net_income)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
